// src/cockpit4/components/HermesDecisionSupport.tsx
// ============================================
// 决策支持（Hermes 助手 P2-功能8）- v2.0 详细改进计划
// 决策信息收集 / 多方案对比 / 风险评估 / 历史案例 / 决策记录复盘
// 数据来源：localStorage（xeaos_hermes_decision）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Scale, FileSearch, History, ShieldAlert, Check } from "lucide-react";

interface DecisionCase {
  id: string;
  topic: string;
  options: { name: string; pros: string; cons: string; score: number; risk: number }[];
  date: string;
  chosen: string;
}

const INITIAL_CASES: DecisionCase[] = [
  {
    id: "dc1", topic: "是否上线「AI 营销增长」模块", date: "2026-07-25", chosen: "方案 A",
    options: [
      { name: "方案 A：独立上线", pros: "聚焦打磨、市场验证快", cons: "无协同、成本高", score: 82, risk: 35 },
      { name: "方案 B：集成到核心", pros: "协同强、复用底层", cons: "耦合度高、迭代慢", score: 76, risk: 50 },
    ],
  },
  {
    id: "dc2", topic: "私有化部署 vs 云托管", date: "2026-07-30", chosen: "方案 B",
    options: [
      { name: "方案 A：私有化部署", pros: "数据安全、定制强", cons: "运维成本高、更新慢", score: 71, risk: 60 },
      { name: "方案 B：云托管", pros: "运维省心、迭代快", cons: "数据合规需评估", score: 85, risk: 30 },
    ],
  },
];

const KEY = "xeaos_hermes_decision";

function load(): DecisionCase[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as DecisionCase[];
  } catch { /* ignore */ }
  return INITIAL_CASES;
}

export default function HermesDecisionSupport() {
  const [cases, setCases] = useState<DecisionCase[]>(load);
  const [selected, setSelected] = useState<string>("dc1");

  const active = cases.find((c) => c.id === selected) ?? cases[0];
  const best = useMemo(() => [...active.options].sort((a, b) => b.score - a.score)[0], [active]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Scale size={14} className="text-indigo-400" />
        <h3 className="text-[12px] font-medium text-white">Hermes 决策支持</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能8 · Decision Support</span>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <FileSearch size={11} className="text-indigo-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">历史决策案例</span>
        </div>
        <div className="flex gap-2 mb-2">
          {cases.map((c) => (
            <button key={c.id} onClick={() => setSelected(c.id)} className={`px-2.5 py-1 rounded-md border text-[9px] font-mono ${c.id === selected ? "border-indigo-500/40 bg-indigo-500/10 text-indigo-300" : "border-white/[0.06] bg-white/[0.02] text-gray-400 hover:text-white"}`}>
              {c.topic}
            </button>
          ))}
        </div>
        <div className="text-[9px] font-mono text-gray-600">决策日期 {active.date} · 已选 {active.chosen}</div>
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-8 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Scale size={11} className="text-indigo-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">多方案对比 · {active.topic}</span>
          </div>
          <div className="space-y-2">
            {active.options.map((o) => {
              const isBest = o.name === best.name;
              return (
                <div key={o.name} className={`rounded-md border p-3 ${isBest ? "border-emerald-500/30 bg-emerald-500/[0.04]" : "border-white/[0.04] bg-white/[0.01]"}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-medium text-white">{o.name}</span>
                    {isBest && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">推荐</span>}
                    <span className="ml-auto text-[10px] font-mono font-semibold text-indigo-300">{o.score} 分</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-1.5 text-[9px] font-mono">
                    <div className="rounded bg-emerald-500/[0.06] p-1.5"><span className="text-emerald-400">优势：</span><span className="text-gray-400">{o.pros}</span></div>
                    <div className="rounded bg-red-500/[0.06] p-1.5"><span className="text-red-400">劣势：</span><span className="text-gray-400">{o.cons}</span></div>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-[9px] font-mono">
                    <span className="text-gray-600">风险评估：</span>
                    <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${o.risk}%`, background: o.risk >= 50 ? "#F87171" : o.risk >= 30 ? "#FBBF24" : "#34D399" }} />
                    </div>
                    <span className="text-gray-500">{o.risk}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="col-span-4 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <History size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">决策记录与复盘</span>
          </div>
          <div className="space-y-1.5 text-[9px] font-mono">
            <div className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-gray-400"><ShieldAlert size={10} className="text-amber-400 mt-0.5 flex-shrink-0" /> 已参考 3 个历史类似案例，信息收集完整度 92%</div>
            <div className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-gray-400"><Check size={10} className="text-emerald-400 mt-0.5 flex-shrink-0" /> 上次决策复盘：选择方案 A 后 30 天验证，实际达成预期 110%</div>
            <button className="mt-2 w-full px-2 py-1 rounded-md border border-indigo-500/30 bg-indigo-500/10 text-indigo-300">为当前议题生成决策分析</button>
          </div>
        </div>
      </div>
    </div>
  );
}

